
import React, { useState, useEffect, useRef } from 'react';
import { Lock, Camera, LayoutGrid, Users, LogOut, Mail, Pencil, Plus, PieChart, CheckCircle, X, Settings, Image as ImageIcon, Loader2, Building, Hash, Database, ShieldAlert, Code, MessageSquare, Trash2, Smartphone, Send, Upload, ShieldCheck, UserCircle, Activity, AlertTriangle, RefreshCw } from 'lucide-react';
import { Property, Agent, Lead, Chat, ChatMessage } from '../types';
import { getSupabase } from '../lib/supabase';
import { INITIAL_AGENTS, COMMUNITY_STRUCTURE } from '../constants';

const Admin: React.FC<{ onUpdateLogo: (l: string) => void; onResetLogo: () => void; currentLogoUrl: string }> = ({ onUpdateLogo, onResetLogo, currentLogoUrl }) => {
  const [currentAgent, setCurrentAgent] = useState<Agent | null>(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [authError, setAuthError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dbStatus, setDbStatus] = useState<'disconnected' | 'connected' | 'error' | 'missing_tables'>('disconnected');
  const [lastError, setLastError] = useState<string | null>(null);
  
  const [leads, setLeads] = useState<Lead[]>([]);
  const [adminProperties, setAdminProperties] = useState<Property[]>([]);
  const [allAgents, setAllAgents] = useState<Agent[]>(INITIAL_AGENTS);
  
  // Diagnostic State
  const [diagResults, setDiagResults] = useState<{table: string, status: 'ok' | 'fail' | 'checking'}[]>([]);

  // Chat State
  const [activeChats, setActiveChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');

  // Form Specific State
  const [selectedRegion, setSelectedRegion] = useState('');
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  
  const initialPropForm: Partial<Property> = { 
    title: '', projectName: '', developer: '', price: 0, currency: 'AED', location: '', type: 'sale', 
    status: 'Active', propertyType: 'Apartment', beds: 1, bedType: '1 BHK', baths: 1, bathType: '1 BATH', 
    sqft: 0, description: '', isFeatured: false, isDistress: false, originalPrice: 0,
    lat: 25.2048, lng: 55.2708, images: [], reraPermitNo: '', referenceId: ''
  };
  
  const [propForm, setPropForm] = useState<Partial<Property>>(initialPropForm);
  const [editingPropId, setEditingPropId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Agent Management State
  const initialAgentForm: Partial<Agent> = { name: '', email: '', role: 'agent', phone: '', whatsapp: '', password: '', photoUrl: '' };
  const [agentForm, setAgentForm] = useState<Partial<Agent>>(initialAgentForm);
  const [editingAgentId, setEditingAgentId] = useState<string | null>(null);
  const [photoMode, setPhotoMode] = useState<'url' | 'upload'>('url');

  // Self Profile State
  const [profileForm, setProfileForm] = useState<Partial<Agent>>({});
  const [profilePhotoMode, setProfilePhotoMode] = useState<'url' | 'upload'>('url');
  
  const formRef = useRef<HTMLDivElement>(null);
  const agentFormRef = useRef<HTMLDivElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const agentPhotoInputRef = useRef<HTMLInputElement>(null);
  const profilePhotoInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    checkDbConnection();
    const saved = localStorage.getItem('guardian_admin_session');
    if (saved) {
      try { 
        const parsed = JSON.parse(saved);
        setCurrentAgent(parsed);
        setProfileForm(parsed);
        setProfilePhotoMode(parsed.photoUrl && parsed.photoUrl.startsWith('data:') ? 'upload' : 'url');
      } catch(e) {}
    }
  }, []);

  useEffect(() => {
    if (dbStatus === 'connected' && currentAgent) {
      fetchAdminData();
      fetchChats();
    }
  }, [dbStatus, currentAgent]);

  useEffect(() => {
    if (selectedChat) {
      fetchChatMessages(selectedChat.id);
      const supabase = getSupabase();
      if (!supabase) return;

      const channel = supabase
        .channel(`admin_chat_${selectedChat.id}`)
        .on('postgres_changes', { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'chat_messages', 
          filter: `chat_id=eq.${selectedChat.id}` 
        }, (payload) => {
          const nm = payload.new as any;
          setChatMessages(prev => {
            if (prev.find(m => m.id === nm.id)) return prev;
            return [...prev, {
              id: nm.id, 
              chatId: nm.chat_id, 
              content: nm.content, 
              senderType: nm.sender_type, 
              createdAt: nm.created_at
            }];
          });
        })
        .subscribe();

      return () => { supabase.removeChannel(channel); };
    }
  }, [selectedChat]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const checkDbConnection = async () => {
    const supabase = getSupabase();
    if (!supabase) return;
    try {
      const { error } = await supabase.from('properties').select('id').limit(1);
      if (error) {
        if (error.code === 'PGRST116') {
          setDbStatus('connected');
        } else {
          setLastError(error.message);
          setDbStatus('missing_tables');
        }
      } else {
        setDbStatus('connected');
        setLastError(null);
      }
    } catch (e: any) { 
      setDbStatus('error');
      setLastError(e.message);
    }
  };

  const runDiagnostics = async () => {
    const supabase = getSupabase();
    if (!supabase) return;
    const tables = ['properties', 'agents', 'leads', 'chats', 'chat_messages'];
    setDiagResults(tables.map(t => ({ table: t, status: 'checking' })));
    
    for (const table of tables) {
      try {
        const { error } = await supabase.from(table).select('count', { count: 'exact', head: true });
        setDiagResults(prev => prev.map(t => t.table === table ? { ...t, status: error ? 'fail' : 'ok' } : t));
      } catch (e) {
        setDiagResults(prev => prev.map(t => t.table === table ? { ...t, status: 'fail' } : t));
      }
    }
  };

  const fetchAdminData = async () => {
    const supabase = getSupabase();
    if (!supabase || !currentAgent) return;
    setLastError(null);
    try {
      let propsQuery = supabase.from('properties').select('*').order('created_at', { ascending: false });
      if (currentAgent.role !== 'admin') {
        propsQuery = propsQuery.eq('agent_id', currentAgent.id);
      }
      
      const { data: props, error: propsErr } = await propsQuery;
      if (propsErr) throw propsErr;

      if (props) setAdminProperties(props.map((p: any) => ({ 
        ...p, imageUrl: p.image_url, projectName: p.project_name, propertyType: p.property_type, agentId: p.agent_id, images: p.images || [] 
      })));
      
      const { data: agents, error: agentErr } = await supabase.from('agents').select('*');
      if (agents) {
        const dbAgents = agents.map(a => ({ ...a, photoUrl: a.photo_url || a.photoUrl }));
        const merged = [...INITIAL_AGENTS];
        dbAgents.forEach(da => { 
          const existingIdx = merged.findIndex(m => m.email.toLowerCase() === da.email.toLowerCase());
          if (existingIdx > -1) merged[existingIdx] = da;
          else merged.push(da);
        });
        setAllAgents(merged);
      }

      let leadsQuery = supabase.from('leads').select('*').order('created_at', { ascending: false });
      if (currentAgent.role !== 'admin' && props) {
        const myPropIds = props.map(p => p.id);
        leadsQuery = leadsQuery.in('property_id', myPropIds);
      }

      const { data: leadData, error: leadErr } = await leadsQuery;
      if (leadErr) throw leadErr;
      if (leadData) setLeads(leadData as any);
    } catch (e: any) {
        console.error("Fetch Error:", e.message);
        setLastError(e.message);
    }
  };

  const fetchChats = async () => {
    const sb = getSupabase();
    if (!sb) return;
    try {
        const { data } = await sb.from('chats').select('*').order('last_message_at', { ascending: false });
        if (data) setActiveChats(data.map(c => ({ 
            id: c.id, clientName: c.client_name, clientEmail: c.client_email, status: c.status, 
            createdAt: c.created_at, lastMessageAt: c.last_message_at 
        })));
    } catch(e) {}
  };

  const fetchChatMessages = async (cid: string) => {
    const sb = getSupabase();
    if (!sb) return;
    try {
        const { data } = await sb.from('chat_messages').select('*').eq('chat_id', cid).order('created_at', { ascending: true });
        if (data) setChatMessages(data.map(m => ({ 
            id: m.id, chatId: m.chat_id, content: m.content, senderType: m.sender_type, createdAt: m.created_at 
        })));
    } catch(e) {}
  };

  const handleAdminChatSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || !selectedChat) return;
    const sb = getSupabase();
    if (!sb) return;
    const msg = chatInput;
    setChatInput('');
    await sb.from('chat_messages').insert({ id: `agent_${Date.now()}`, chat_id: selectedChat.id, content: msg, sender_type: 'agent' });
    await sb.from('chats').update({ last_message_at: new Date().toISOString() }).eq('id', selectedChat.id);
  };

  const handleGalleryUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    (Array.from(files) as File[]).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => setGalleryImages(prev => [...prev, reader.result as string]);
      reader.readAsDataURL(file);
    });
  };

  const handleAgentPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setAgentForm(prev => ({ ...prev, photoUrl: reader.result as string }));
    reader.readAsDataURL(file);
  };

  const handleProfilePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setProfileForm(prev => ({ ...prev, photoUrl: reader.result as string }));
    reader.readAsDataURL(file);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setIsLoggingIn(true);
    const cleanedEmail = loginEmail.trim().toLowerCase();
    const cleanedPass = loginPass.trim();

    try {
      const supabase = getSupabase();
      if (!supabase) { setAuthError('Supabase configuration missing.'); return; }
      
      // 1. Prioritize Database Login (allows profile persistence)
      const { data, error } = await supabase.from('agents').select('*').eq('email', cleanedEmail).eq('password', cleanedPass).maybeSingle();
      
      if (data && !error) {
        const foundAgent: Agent = { ...data, photoUrl: data.photo_url || data.photoUrl || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=200' };
        setCurrentAgent(foundAgent);
        setProfileForm(foundAgent);
        localStorage.setItem('guardian_admin_session', JSON.stringify(foundAgent));
        window.dispatchEvent(new CustomEvent('guardian-login-state-changed'));
      } else { 
        // 2. Fallback to hardcoded only if DB check fails or no match found
        const hardcoded = INITIAL_AGENTS.find(a => a.email.toLowerCase() === cleanedEmail && a.password === cleanedPass);
        if (hardcoded) {
          setCurrentAgent(hardcoded);
          setProfileForm(hardcoded);
          localStorage.setItem('guardian_admin_session', JSON.stringify(hardcoded));
          window.dispatchEvent(new CustomEvent('guardian-login-state-changed'));
        } else {
          setAuthError(error ? `DB Error: ${error.message}` : 'Access Denied. Invalid credentials.'); 
        }
      }
    } catch (err) { setAuthError('System error during authentication.'); } finally { setIsLoggingIn(false); }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentAgent) return;
    const supabase = getSupabase();
    if (!supabase) return;
    setIsSubmitting(true);
    
    const payload = {
      name: profileForm.name, 
      phone: profileForm.phone, 
      whatsapp: profileForm.whatsapp,
      password: profileForm.password, 
      photo_url: profileForm.photoUrl
    };

    // Check if the agent exists in DB, if not, we must create them first
    const { data: existing } = await supabase.from('agents').select('id').eq('id', currentAgent.id).maybeSingle();
    
    let error;
    if (existing) {
      const res = await supabase.from('agents').update(payload).eq('id', currentAgent.id);
      error = res.error;
    } else {
      // Hardcoded agent being "promoted" to DB record
      const res = await supabase.from('agents').insert([{ ...payload, email: currentAgent.email, role: currentAgent.role, id: currentAgent.id }]);
      error = res.error;
    }

    if (!error) {
      const updatedAgent = { ...currentAgent, ...payload, photoUrl: payload.photo_url };
      setCurrentAgent(updatedAgent);
      localStorage.setItem('guardian_admin_session', JSON.stringify(updatedAgent));
      window.dispatchEvent(new CustomEvent('guardian-login-state-changed'));
      setIsSuccess(true);
      fetchAdminData();
      setTimeout(() => setIsSuccess(false), 3000);
    } else { alert("Update failed: " + error.message); }
    setIsSubmitting(false);
  };

  const onPropFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (galleryImages.length === 0) { alert("Visual inventory requires at least one image."); return; }
    setIsSubmitting(true);
    const supabase = getSupabase();
    if (!supabase || !currentAgent) return;
    const assignedAgentId = editingPropId ? (adminProperties.find(p => p.id === editingPropId)?.agentId || currentAgent.id) : currentAgent.id;
    
    const payload = {
      title: propForm.title, 
      project_name: propForm.projectName, 
      developer: propForm.developer, 
      price: propForm.price, 
      currency: propForm.currency, 
      location: propForm.location,
      type: propForm.type, 
      beds: propForm.beds, 
      bed_type: propForm.bedType, 
      baths: propForm.baths, 
      bath_type: propForm.bathType, 
      sqft: propForm.sqft,
      image_url: galleryImages[0], 
      images: galleryImages, 
      description: propForm.description, 
      is_featured: propForm.isFeatured,
      is_distress: propForm.isDistress, 
      original_price: propForm.originalPrice, 
      property_type: propForm.propertyType, 
      agent_id: assignedAgentId, 
      rera_permit_no: propForm.reraPermitNo, 
      reference_id: propForm.referenceId
    };

    try {
        let res;
        if (editingPropId) res = await supabase.from('properties').update(payload).eq('id', editingPropId);
        else res = await supabase.from('properties').insert({ ...payload, id: `prop_${Date.now()}` });
        
        if (!res.error) { 
            setIsSuccess(true); 
            fetchAdminData(); 
            resetPropForm(); 
            setTimeout(() => setIsSuccess(false), 3000); 
        } else { throw res.error; }
    } catch(err: any) {
        alert("Portfolio Update Failed: " + (err.message || "Unknown error"));
    } finally {
        setIsSubmitting(false);
    }
  };

  const handleAgentFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const supabase = getSupabase();
    if (!supabase) return;
    setIsSubmitting(true);

    const payload = { 
      name: agentForm.name, 
      email: agentForm.email?.trim().toLowerCase(), 
      role: agentForm.role || 'agent', 
      phone: agentForm.phone, 
      whatsapp: agentForm.whatsapp, 
      password: agentForm.password, 
      photo_url: agentForm.photoUrl || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=200' 
    };

    try {
        let error;
        if (editingAgentId) { 
            const res = await supabase.from('agents').update(payload).eq('id', editingAgentId); 
            error = res.error; 
        } else { 
            const res = await supabase.from('agents').insert([{ ...payload, id: `agent_${Date.now()}` }]); 
            error = res.error; 
        }
        
        if (!error) { 
            fetchAdminData(); 
            resetAgentForm(); 
            setIsSuccess(true); 
            setTimeout(() => setIsSuccess(false), 3000); 
        } else { throw error; }
    } catch(err: any) {
        alert("Personnel Registration Failed: " + (err.message || "Unknown error"));
    } finally {
        setIsSubmitting(false);
    }
  };

  const resetAgentForm = () => { setAgentForm(initialAgentForm); setEditingAgentId(null); setPhotoMode('url'); };
  const resetPropForm = () => { setPropForm(initialPropForm); setEditingPropId(null); setGalleryImages([]); setSelectedRegion(''); };
  const filteredCommunities = COMMUNITY_STRUCTURE.find(c => c.label === selectedRegion)?.subItems || [];

  const allTabs = [
    { id: 'dashboard', icon: PieChart, label: 'Performance', roles: ['admin', 'agent'] },
    { id: 'properties', icon: LayoutGrid, label: 'Portfolio', roles: ['admin', 'agent'] },
    { id: 'profile', icon: UserCircle, label: 'My Profile', roles: ['admin', 'agent'] },
    { id: 'agents', icon: Users, label: 'Team', roles: ['admin'] },
    { id: 'chats', icon: Send, label: 'Live', roles: ['admin', 'agent'] },
    { id: 'leads', icon: MessageSquare, label: 'Leads', roles: ['admin', 'agent'] },
    { id: 'settings', icon: Settings, label: 'System', roles: ['admin'] }
  ];
  const visibleTabs = allTabs.filter(t => currentAgent && t.roles.includes(currentAgent.role));

  if (!currentAgent) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gold-500 shadow-lg shadow-gold-500/50"></div>
        <div className="bg-white p-10 rounded-sm shadow-2xl max-w-md w-full relative z-10 border-b-8 border-slate-800">
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-gold-500/30 shadow-2xl">
              <ShieldCheck className="text-gold-500" size={36}/>
            </div>
            <h1 className="text-3xl font-serif text-slate-900 font-bold tracking-tight">Guardian Team Access</h1>
            <p className="text-gray-400 text-[10px] uppercase tracking-[0.3em] mt-3 font-bold border-t border-gray-100 pt-3 mx-8">Authorized Personnel Only</p>
          </div>
          {authError && (
            <div className="bg-red-50 text-red-600 p-4 rounded-sm text-[10px] font-bold uppercase mb-6 text-center border border-red-100 animate-pulse flex items-center justify-center gap-2">
              <ShieldAlert size={14}/> {authError}
            </div>
          )}
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-1">
              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest ml-1">Member Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={16}/>
                <input type="email" placeholder="name@guardianhousing.ae" required value={loginEmail} onChange={e=>setLoginEmail(e.target.value)} className="w-full bg-slate-50 border-b-2 border-slate-100 p-3 pl-10 outline-none focus:border-gold-500 text-sm transition-all focus:bg-white" />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest ml-1">Portal Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={16}/>
                <input type="password" placeholder="••••••••" required value={loginPass} onChange={e=>setLoginPass(e.target.value)} className="w-full bg-slate-50 border-b-2 border-slate-100 p-3 pl-10 outline-none focus:border-gold-500 text-sm transition-all focus:bg-white" />
              </div>
            </div>
            <button disabled={isLoggingIn} className="w-full bg-slate-900 text-white py-5 rounded-sm font-bold uppercase tracking-[0.2em] text-[10px] hover:bg-gold-500 transition-all shadow-xl hover:shadow-gold-500/20 active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3 mt-4">
              {isLoggingIn ? <Loader2 size={16} className="animate-spin"/> : 'Secure Authentication'}
            </button>
          </form>
          <div className="mt-10 text-center"><p className="text-[9px] text-gray-300 font-bold uppercase tracking-widest">Property of Guardian Housing Real Estate LLC</p></div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-20 min-h-screen bg-slate-50 font-sans">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="bg-white rounded p-8 flex flex-col md:flex-row justify-between items-center mb-6 border-b-4 border-gold-500 shadow-sm">
           <div className="flex items-center gap-6">
              <div className="relative">
                <img src={currentAgent.photoUrl} className="w-16 h-16 rounded-full border-2 border-gold-500 object-cover shadow-lg" alt="Admin" />
                <div className="absolute -bottom-1 -right-1 bg-green-500 w-4 h-4 rounded-full border-2 border-white"></div>
              </div>
              <div>
                <h2 className="text-xl font-serif text-slate-900 font-bold">{currentAgent.name}</h2>
                <p className="text-[10px] text-gold-600 uppercase tracking-widest font-bold">Portal {currentAgent.role}</p>
              </div>
           </div>
           <div className="flex gap-3">
             <button onClick={() => fetchAdminData()} className="p-3 bg-slate-100 hover:bg-slate-200 text-gray-600 rounded-full transition-all flex items-center gap-2 px-4">
                <RefreshCw size={16} className={isSubmitting ? 'animate-spin' : ''}/>
                <span className="text-[10px] font-bold uppercase tracking-widest hidden md:inline">Refresh Data</span>
             </button>
             <button onClick={() => { setCurrentAgent(null); localStorage.removeItem('guardian_admin_session'); window.dispatchEvent(new CustomEvent('guardian-login-state-changed')); }} className="p-3 bg-slate-100 hover:bg-red-50 text-gray-400 hover:text-red-600 rounded-full transition-all">
               <LogOut size={20}/>
             </button>
           </div>
        </div>

        {lastError && (
          <div className="bg-red-50 border border-red-100 p-4 rounded mb-6 flex items-center gap-3 text-red-700 animate-fade-in shadow-sm">
             <AlertTriangle size={20}/>
             <div>
                <p className="text-xs font-bold uppercase tracking-widest">Database Communication Error</p>
                <p className="text-[11px] font-mono opacity-80">{lastError}</p>
             </div>
             <button onClick={() => { checkDbConnection(); fetchAdminData(); }} className="ml-auto text-[9px] font-bold uppercase underline">Retry Connection</button>
          </div>
        )}

        <div className="flex gap-2 mb-8 bg-white p-2 rounded shadow-sm overflow-x-auto no-scrollbar">
            {visibleTabs.map(tab => (
               <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-6 py-3 text-[10px] font-bold uppercase tracking-widest transition-all rounded-sm flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id ? 'bg-slate-900 text-white shadow-lg' : 'text-gray-400 hover:bg-slate-50'}`}>
                 <tab.icon size={16}/> {tab.label}
               </button>
            ))}
        </div>

        {activeTab === 'dashboard' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in">
             <div className="bg-white p-8 rounded border border-gray-100 shadow-sm relative overflow-hidden group">
                <LayoutGrid className="absolute -right-4 -bottom-4 text-slate-50 w-24 h-24 group-hover:text-gold-50 transition-colors"/>
                <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest mb-1 relative z-10">{currentAgent.role === 'admin' ? 'Total Portfolio Assets' : 'My Active Listings'}</p>
                <h4 className="text-4xl font-serif font-bold text-slate-900 relative z-10">{adminProperties.length}</h4>
             </div>
             <div className="bg-white p-8 rounded border border-gray-100 shadow-sm relative overflow-hidden group">
                <MessageSquare className="absolute -right-4 -bottom-4 text-slate-50 w-24 h-24 group-hover:text-gold-50 transition-colors"/>
                <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest mb-1 relative z-10">{currentAgent.role === 'admin' ? 'Total Leads Captured' : 'Leads for My Properties'}</p>
                <h4 className="text-4xl font-serif font-bold text-slate-900 relative z-10">{leads.length}</h4>
             </div>
             <div className="bg-white p-8 rounded border border-gray-100 shadow-sm relative overflow-hidden group">
                <Activity className="absolute -right-4 -bottom-4 text-slate-50 w-24 h-24 group-hover:text-gold-50 transition-colors"/>
                <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest mb-1 relative z-10">Infrastructure</p>
                <h4 className={`text-xl font-bold flex items-center gap-2 relative z-10 ${dbStatus === 'connected' ? 'text-green-600' : (dbStatus === 'missing_tables' ? 'text-orange-500' : 'text-red-500')}`}>
                   {dbStatus === 'missing_tables' && <Activity size={18}/>}
                   {dbStatus.toUpperCase().replace('_', ' ')}
                </h4>
             </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="max-w-3xl mx-auto animate-fade-in">
            <div className="bg-white p-10 rounded border-t-8 border-gold-500 shadow-2xl">
               <div className="flex items-center gap-4 mb-10 pb-6 border-b">
                  <div className="p-3 bg-slate-900 text-gold-500 rounded-full shadow-inner"><UserCircle size={28}/></div>
                  <div>
                    <h3 className="text-3xl font-serif font-bold text-slate-900">Manage My Identity</h3>
                    <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1 font-bold">Secure Personnel Management</p>
                  </div>
               </div>
               
               <form onSubmit={handleProfileUpdate} className="space-y-8">
                  <div className="grid md:grid-cols-2 gap-8">
                     <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Full Name</label>
                        <input type="text" required value={profileForm.name || ''} onChange={e=>setProfileForm({...profileForm, name: e.target.value})} className="w-full border p-3 text-sm rounded outline-none focus:border-gold-500 bg-slate-50/50" />
                     </div>
                     <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Member Email (Disabled)</label>
                        <input type="email" disabled value={profileForm.email || ''} className="w-full border p-3 text-sm rounded outline-none bg-slate-100 text-gray-400 cursor-not-allowed" />
                     </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">
                     <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Update Password</label>
                        <input type="password" required value={profileForm.password || ''} onChange={e=>setProfileForm({...profileForm, password: e.target.value})} className="w-full border p-3 text-sm rounded outline-none focus:border-gold-500 bg-slate-50/50" />
                     </div>
                     <div className="space-y-1">
                        <div className="flex justify-between items-center mb-1">
                           <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Profile Photo</label>
                           <div className="flex gap-2">
                              <button type="button" onClick={() => setProfilePhotoMode('url')} className={`text-[8px] font-bold uppercase p-1 rounded ${profilePhotoMode === 'url' ? 'bg-gold-500 text-white' : 'bg-slate-100 text-slate-400'}`}>URL</button>
                              <button type="button" onClick={() => setProfilePhotoMode('upload')} className={`text-[8px] font-bold uppercase p-1 rounded ${profilePhotoMode === 'upload' ? 'bg-gold-500 text-white' : 'bg-slate-100 text-slate-400'}`}>Upload</button>
                           </div>
                        </div>
                        {profilePhotoMode === 'url' ? (
                           <input type="text" placeholder="https://..." value={profileForm.photoUrl || ''} onChange={e=>setProfileForm({...profileForm, photoUrl: e.target.value})} className="w-full border p-3 text-sm rounded outline-none focus:border-gold-500 bg-slate-50/50" />
                        ) : (
                           <div className="flex items-center gap-3">
                              <button type="button" onClick={() => profilePhotoInputRef.current?.click()} className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest border border-dashed border-slate-300 w-full p-2.5 rounded hover:border-gold-500 transition-all text-gray-400">
                                 <Upload size={14}/> {profileForm.photoUrl && profileForm.photoUrl.startsWith('data:') ? 'Change Photo' : 'Choose File'}
                              </button>
                              {profileForm.photoUrl && <img src={profileForm.photoUrl} className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-md"/>}
                           </div>
                        )}
                        <input type="file" ref={profilePhotoInputRef} accept="image/*" className="hidden" onChange={handleProfilePhotoUpload} />
                     </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">
                     <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Phone Number</label>
                        <input type="text" placeholder="+971..." value={profileForm.phone || ''} onChange={e=>setProfileForm({...profileForm, phone: e.target.value})} className="w-full border p-3 text-sm rounded outline-none focus:border-gold-500 bg-slate-50/50" />
                     </div>
                     <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">WhatsApp Identity</label>
                        <input type="text" placeholder="971..." value={profileForm.whatsapp || ''} onChange={e=>setProfileForm({...profileForm, whatsapp: e.target.value})} className="w-full border p-3 text-sm rounded outline-none focus:border-gold-500 bg-slate-50/50" />
                     </div>
                  </div>

                  <button disabled={isSubmitting} className="w-full bg-slate-900 text-white py-5 rounded-sm font-bold uppercase tracking-[0.2em] text-xs hover:bg-gold-500 transition-all shadow-2xl flex items-center justify-center gap-3 mt-4">
                    {isSubmitting ? <Loader2 size={16} className="animate-spin"/> : 'Commit Profile Updates'}
                  </button>
               </form>
            </div>
          </div>
        )}

        {activeTab === 'agents' && currentAgent.role === 'admin' && (
          <div className="space-y-8 animate-fade-in">
             <div ref={agentFormRef} className="bg-white p-10 rounded border border-gray-100 shadow-sm">
                <div className="flex justify-between items-center mb-10 pb-4 border-b">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-900 text-white rounded shadow-lg"><Plus size={18}/></div>
                    <h3 className="text-2xl font-serif font-bold text-slate-900">{editingAgentId ? 'Update Agent Profile' : 'Onboard Team Member'}</h3>
                  </div>
                  {editingAgentId && <button onClick={resetAgentForm} className="text-red-500 text-[10px] uppercase font-bold tracking-widest border border-red-50 px-4 py-2 rounded hover:bg-red-50 transition-all">Cancel Edit</button>}
                </div>
                <form onSubmit={handleAgentFormSubmit} className="space-y-8">
                   <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                      <div className="space-y-1">
                          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Full Name</label>
                          <input type="text" placeholder="e.g. John Doe" required value={agentForm.name} onChange={e=>setAgentForm({...agentForm, name: e.target.value})} className="w-full border p-3 text-sm rounded outline-none focus:border-gold-500 transition-all bg-slate-50/50" />
                      </div>
                      <div className="space-y-1">
                          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Email (System Login)</label>
                          <input type="email" placeholder="john@guardianhousing.ae" required value={agentForm.email} onChange={e=>setAgentForm({...agentForm, email: e.target.value})} className="w-full border p-3 text-sm rounded outline-none focus:border-gold-500 transition-all bg-slate-50/50" />
                      </div>
                      <div className="space-y-1">
                          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Portal Password</label>
                          <input type="password" placeholder="••••••••" required value={agentForm.password} onChange={e=>setAgentForm({...agentForm, password: e.target.value})} className="w-full border p-3 text-sm rounded outline-none focus:border-gold-500 transition-all bg-slate-50/50" />
                      </div>
                      <div className="space-y-1">
                          <div className="flex justify-between items-center mb-1">
                             <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Profile Photo</label>
                             <div className="flex gap-2">
                                <button type="button" onClick={() => setPhotoMode('url')} className={`text-[8px] font-bold uppercase p-1 rounded ${photoMode === 'url' ? 'bg-gold-500 text-white' : 'bg-slate-100 text-slate-400'}`}>URL</button>
                                <button type="button" onClick={() => setPhotoMode('upload')} className={`text-[8px] font-bold uppercase p-1 rounded ${photoMode === 'upload' ? 'bg-gold-500 text-white' : 'bg-slate-100 text-slate-400'}`}>Upload</button>
                             </div>
                          </div>
                          {photoMode === 'url' ? (
                             <input type="text" placeholder="https://..." value={agentForm.photoUrl} onChange={e=>setAgentForm({...agentForm, photoUrl: e.target.value})} className="w-full border p-3 text-sm rounded outline-none focus:border-gold-500 transition-all bg-slate-50/50" />
                          ) : (
                             <div className="flex items-center gap-2">
                                <button type="button" onClick={() => agentPhotoInputRef.current?.click()} className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest border border-dashed border-slate-300 w-full p-2.5 rounded hover:border-gold-500 transition-all text-gray-400">
                                   <Upload size={14}/> {agentForm.photoUrl && (agentForm.photoUrl.startsWith('data:') || agentForm.photoUrl.startsWith('http')) ? 'Change Photo' : 'Choose File'}
                                </button>
                                {agentForm.photoUrl && <img src={agentForm.photoUrl} className="w-10 h-10 rounded-full object-cover border"/>}
                             </div>
                          )}
                          <input type="file" ref={agentPhotoInputRef} accept="image/*" className="hidden" onChange={handleAgentPhotoUpload} />
                      </div>
                   </div>
                   <div className="grid md:grid-cols-3 gap-8 pt-4 border-t border-slate-50">
                      <div className="space-y-1">
                          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Phone Number</label>
                          <input type="text" placeholder="+971..." value={agentForm.phone} onChange={e=>setAgentForm({...agentForm, phone: e.target.value})} className="w-full border p-3 text-sm rounded outline-none focus:border-gold-500 transition-all bg-slate-50/50" />
                      </div>
                      <div className="space-y-1">
                          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">WhatsApp Identity</label>
                          <input type="text" placeholder="971..." value={agentForm.whatsapp} onChange={e=>setAgentForm({...agentForm, whatsapp: e.target.value})} className="w-full border p-3 text-sm rounded outline-none focus:border-gold-500 transition-all bg-slate-50/50" />
                      </div>
                      <div className="flex items-end"><button disabled={isSubmitting} className="w-full bg-slate-900 text-white h-[46px] text-[10px] font-bold uppercase tracking-widest rounded hover:bg-gold-500 transition-all flex items-center justify-center gap-3 shadow-2xl">{isSubmitting ? <Loader2 size={16} className="animate-spin"/> : (editingAgentId ? 'Synchronize Agent Profile' : 'Register New Agent')}</button></div>
                   </div>
                </form>
             </div>
             <div className="bg-white rounded border border-gray-100 shadow-sm overflow-hidden animate-fade-in">
                <div className="p-6 bg-slate-50 border-b flex justify-between items-center"><h4 className="font-serif text-xl font-bold text-slate-900">Guardian Field Team</h4><span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{allAgents.length} Active Personnel</span></div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                       <thead className="bg-slate-50/80 border-b"><tr className="text-[10px] font-bold uppercase tracking-widest text-gray-400"><th className="p-6">Agent Identity</th><th className="p-6">Contact Channels</th><th className="p-6">System Access</th><th className="p-6">Presence</th><th className="p-6 text-center">Actions</th></tr></thead>
                       <tbody>
                          {allAgents.map(a => (
                            <tr key={a.id} className="border-b hover:bg-slate-50/50 transition-colors">
                               <td className="p-6 flex items-center gap-4"><img src={a.photoUrl} className="w-12 h-12 rounded-full border-2 border-slate-100 object-cover shadow-sm" alt={a.name} /><div><span className="font-bold text-slate-800 block leading-none">{a.name}</span><span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-1 block">ID: {a.id.slice(-6)}</span></div></td>
                               <td className="p-6"><div className="text-slate-600 text-xs font-medium">{a.email}</div><div className="text-gray-400 text-[10px] mt-1 flex items-center gap-1.5"><Smartphone size={10}/> {a.phone || 'No direct line'}</div></td>
                               <td className="p-6"><span className={`text-[9px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border ${a.role === 'admin' ? 'bg-slate-900 text-gold-400 border-gold-500/30' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>{a.role}</span></td>
                               <td className="p-6"><span className="text-green-600 text-[10px] font-bold uppercase flex items-center gap-2"><div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div> Verified</span></td>
                               <td className="p-6"><div className="flex justify-center gap-4"><button onClick={() => { setEditingAgentId(a.id); setAgentForm(a); setPhotoMode(a.photoUrl && (a.photoUrl.startsWith('data:') || a.photoUrl.startsWith('http')) ? 'upload' : 'url'); window.scrollTo({ top: agentFormRef.current?.offsetTop ? agentFormRef.current.offsetTop - 120 : 0, behavior: 'smooth' }); }} className="p-2.5 text-gold-600 hover:bg-gold-50 rounded shadow-sm border border-gold-100 transition-all" title="Edit Agent"><Pencil size={16}/></button><button onClick={async () => { if (confirm(`Are you sure you want to remove ${a.name} from the system? This cannot be undone.`)) { const sb = getSupabase(); if (sb) { const { error } = await sb.from('agents').delete().eq('id', a.id); if (error) alert(error.message); else fetchAdminData(); } } }} className="p-2.5 text-red-400 hover:bg-red-50 rounded shadow-sm border border-red-100 transition-all" title="Delete Agent"><Trash2 size={16}/></button></div></td>
                            </tr>
                          ))}
                       </tbody>
                    </table>
                </div>
             </div>
          </div>
        )}

        {activeTab === 'properties' && (
          <div className="space-y-12 animate-fade-in">
             <div ref={formRef} className="bg-white p-10 rounded border-t-[10px] border-gold-500 shadow-2xl">
                <div className="flex justify-between items-center mb-10 pb-6 border-b"><div><h3 className="text-3xl font-serif font-bold text-slate-900">{editingPropId ? 'Modify Asset Details' : 'Publish Exclusive Inventory'}</h3><p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1 font-bold">Comprehensive Portfolio Entry v5.0</p></div>{editingPropId && <button onClick={resetPropForm} className="text-red-500 text-[10px] uppercase font-bold tracking-widest border border-red-100 px-4 py-2 rounded hover:bg-red-50">Cancel Edit</button>}</div>
                <form onSubmit={onPropFormSubmit} className="space-y-12">
                   <div className="bg-slate-50 p-6 rounded border border-dashed border-gray-300"><label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4 block">Property Asset Gallery (Upload Multiple)</label><div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">{galleryImages.map((img, idx) => (<div key={idx} className="relative aspect-square rounded overflow-hidden shadow-lg group"><img src={img} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="Preview" /><button type="button" onClick={() => setGalleryImages(prev => prev.filter((_, i) => i !== idx))} className="absolute top-1 right-1 bg-red-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><X size={12}/></button></div>))}<button type="button" onClick={() => galleryInputRef.current?.click()} className="aspect-square rounded border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:text-gold-500 hover:border-gold-500 hover:bg-white transition-all"><Camera size={24} className="mb-2"/><span className="text-[9px] font-bold uppercase tracking-widest">Add Media</span></button></div><input type="file" ref={galleryInputRef} multiple accept="image/*" onChange={handleGalleryUpload} className="hidden" /></div>
                   <div className="grid lg:grid-cols-2 gap-12"><div className="space-y-6"><label className="text-[10px] font-bold text-gold-600 uppercase tracking-widest border-b border-gold-100 pb-2 block">General Particulars</label><input type="text" placeholder="Descriptive Listing Title" required value={propForm.title} onChange={e=>setPropForm({...propForm, title: e.target.value})} className="w-full border-b py-3 text-xl font-serif focus:border-gold-500 outline-none transition-all" /><div className="grid grid-cols-2 gap-6"><div className="space-y-1"><label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Region / Category</label><select required value={selectedRegion} onChange={e=>setSelectedRegion(e.target.value)} className="w-full border p-3 text-sm rounded outline-none focus:border-gold-500"><option value="">Select Region</option>{COMMUNITY_STRUCTURE.map(c => <option key={c.label} value={c.label}>{c.label}</option>)}</select></div><div className="space-y-1"><label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Target Community</label><select required disabled={!selectedRegion} value={propForm.location} onChange={e=>setPropForm({...propForm, location: e.target.value})} className="w-full border p-3 text-sm rounded outline-none focus:border-gold-500 disabled:bg-gray-50"><option value="">Choose Area</option>{filteredCommunities.map(si => <option key={si.label} value={si.label}>{si.label}</option>)}</select></div></div><div className="grid grid-cols-2 gap-6"><input type="text" placeholder="Project Name" value={propForm.projectName} onChange={e=>setPropForm({...propForm, projectName: e.target.value})} className="border p-3 text-sm rounded outline-none focus:border-gold-500" /><input type="text" placeholder="Developer Entity" value={propForm.developer} onChange={e=>setPropForm({...propForm, developer: e.target.value})} className="border p-3 text-sm rounded outline-none focus:border-gold-500" /></div><textarea placeholder="Describe the lifestyle and premium features of this property..." value={propForm.description} onChange={e=>setPropForm({...propForm, description: e.target.value})} className="w-full border p-4 text-sm rounded h-40 outline-none focus:border-gold-500 resize-none" /></div><div className="space-y-6"><label className="text-[10px] font-bold text-gold-600 uppercase tracking-widest border-b border-gold-100 pb-2 block">Specifications & Valuation</label><div className="grid grid-cols-3 gap-3"><select value={propForm.type} onChange={e=>setPropForm({...propForm, type: e.target.value as any})} className="border p-3 text-sm rounded outline-none font-bold uppercase text-[10px]"><option value="sale">For Sale</option><option value="rent">For Rent</option><option value="off-plan">For Off-Plan</option><option value="uk-investment">For UK Investment</option></select><input type="number" placeholder="Asking Price" required value={propForm.price || ''} onChange={e=>setPropForm({...propForm, price: parseFloat(e.target.value)})} className="col-span-2 border p-3 text-sm rounded outline-none focus:border-gold-500 font-bold" /></div><div className="grid grid-cols-2 gap-4"><div className="space-y-1"><label className="text-[9px] font-bold text-gray-400 uppercase">Unit Type</label><select value={propForm.propertyType} onChange={e=>setPropForm({...propForm, propertyType: e.target.value})} className="w-full border p-3 text-sm rounded outline-none">{['Apartment', 'Villa', 'Townhouse', 'Penthouse', 'Land'].map(t => <option key={t} value={t}>{t}</option>)}</select></div><div className="space-y-1"><label className="text-[9px] font-bold text-gray-400 uppercase">Bedroom Count</label><select value={propForm.beds} onChange={e=>setPropForm({...propForm, beds: parseInt(e.target.value)})} className="w-full border p-3 text-sm rounded outline-none">{[0, 1, 2, 3, 4, 5, 6, 7].map(n => <option key={n} value={n}>{n === 0 ? 'Studio' : n + ' Bed'}</option>)}</select></div></div><div className="grid grid-cols-2 gap-4"><div className="space-y-1"><label className="text-[9px] font-bold text-gray-400 uppercase">RERA Permit No.</label><input type="text" placeholder="e.g. 7122..." value={propForm.reraPermitNo} onChange={e=>setPropForm({...propForm, reraPermitNo: e.target.value})} className="w-full border p-3 text-sm rounded outline-none" /></div><div className="space-y-1"><label className="text-[9px] font-bold text-gray-400 uppercase">Guardian Ref.</label><input type="text" placeholder="e.g. GDN-D-..." value={propForm.referenceId} onChange={e=>setPropForm({...propForm, referenceId: e.target.value})} className="w-full border p-3 text-sm rounded outline-none" /></div></div><div className="flex gap-4 p-4 bg-slate-900 text-white rounded"><div className="flex items-center gap-2"><input type="checkbox" checked={propForm.isFeatured} onChange={e=>setPropForm({...propForm, isFeatured: e.target.checked})} id="isFeatured" className="w-4 h-4 accent-gold-500" /><label htmlFor="isFeatured" className="text-[10px] font-bold uppercase tracking-widest cursor-pointer">Featured Asset</label></div><div className="flex items-center gap-2"><input type="checkbox" checked={propForm.isDistress} onChange={e=>setPropForm({...propForm, isDistress: e.target.checked})} id="isDistress" className="w-4 h-4 accent-red-500" /><label htmlFor="isDistress" className="text-[10px] font-bold uppercase tracking-widest cursor-pointer text-red-400">Distress Deal</label></div></div><button type="submit" disabled={isSubmitting} className="w-full bg-slate-900 text-white py-5 font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-gold-500 transition-all flex items-center justify-center gap-3 shadow-xl">{isSubmitting ? <Loader2 className="animate-spin" size={18}/> : (editingPropId ? 'Sync Updates' : 'Publish Asset to Global Site')}</button></div></div>
                </form>
             </div>
             <div className="bg-white rounded border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
                  <h3 className="font-serif text-xl font-bold">{currentAgent.role === 'admin' ? 'Manage Live Portfolio' : 'My Portfolio Inventory'}</h3>
                  <div className="flex items-center gap-2 text-xs text-gray-400"><Database size={14}/> {adminProperties.length} Records</div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 border-b"><tr className="text-[10px] font-bold uppercase text-gray-400"><th className="p-6">Thumbnail</th><th className="p-6">Property Description</th><th className="p-6">Type / Market</th><th className="p-6">List Price</th><th className="p-6 text-center">Actions</th></tr></thead>
                    <tbody>
                      {adminProperties.map(p => (
                        <tr key={p.id} className="border-b hover:bg-slate-50 transition-all">
                          <td className="p-6"><img src={p.images?.[0] || p.imageUrl} className="w-20 h-14 object-cover rounded shadow-sm" alt="Asset" /></td>
                          <td className="p-6"><div className="font-bold text-slate-900">{p.title}</div><div className="text-[10px] text-gray-400 uppercase tracking-widest mt-0.5">{p.location}</div></td>
                          <td className="p-6"><span className={`text-[9px] font-bold uppercase px-2 py-1 rounded-sm border ${p.type === 'sale' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-blue-50 text-blue-700 border-blue-100'}`}>{p.type}</span></td>
                          <td className="p-6 font-bold text-slate-900">{p.price.toLocaleString()} {p.currency}</td>
                          <td className="p-6"><div className="flex justify-center gap-3"><button onClick={() => { setEditingPropId(p.id); setPropForm(p); setGalleryImages(p.images || [p.imageUrl]); window.scrollTo({ top: formRef.current?.offsetTop ? formRef.current.offsetTop - 120 : 0, behavior: 'smooth' }); }} className="p-2 text-gold-600 hover:bg-gold-50 rounded transition-all"><Pencil size={18}/></button><button onClick={async () => { if(confirm("Permanently delete this asset?")) { const sb = getSupabase(); if(sb) await sb.from('properties').delete().eq('id', p.id); fetchAdminData(); } }} className="p-2 text-red-400 hover:bg-red-50 rounded transition-all"><Trash2 size={18}/></button></div></td>
                        </tr>
                      ))}
                      {adminProperties.length === 0 && (
                        <tr><td colSpan={5} className="p-20 text-center text-gray-400 italic">No assets currently found in your assigned portfolio.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
             </div>
          </div>
        )}

        {activeTab === 'settings' && currentAgent.role === 'admin' && (
           <div className="space-y-8 animate-fade-in">
              <div className="bg-slate-900 text-white p-10 rounded border-l-[10px] border-orange-500 shadow-2xl">
                 <div className="flex items-center gap-4 mb-6 text-orange-500">
                    <ShieldAlert size={28}/>
                    <h3 className="text-2xl font-serif font-bold">SQL Database Optimization</h3>
                 </div>
                 <p className="text-gray-400 text-sm mb-8 max-w-2xl leading-relaxed">Ensure all premium features including multiple image galleries, RERA integration, and agent profile linking are operational by executing the latest schema upgrade.</p>
                 <div className="bg-black/50 p-6 rounded text-[11px] font-mono text-green-500 overflow-x-auto border border-white/5 mb-8">
                    <pre>{`ALTER TABLE properties 
ADD COLUMN IF NOT EXISTS project_name TEXT,
ADD COLUMN IF NOT EXISTS developer TEXT,
ADD COLUMN IF NOT EXISTS images TEXT[],
ADD COLUMN IF NOT EXISTS rera_permit_no TEXT,
ADD COLUMN IF NOT EXISTS reference_id TEXT;

CREATE TABLE IF NOT EXISTS agents (
    id TEXT PRIMARY KEY,
    name TEXT NOT NULL,
    email TEXT UNIQUE NOT NULL,
    phone TEXT,
    whatsapp TEXT,
    photo_url TEXT,
    role TEXT DEFAULT 'agent',
    password TEXT,
    created_at TIMESTAMP WITH TIME ZONE DEFAULT CURRENT_TIMESTAMP
);`}</pre>
                 </div>
                 <div className="flex flex-wrap gap-4">
                    <button onClick={() => { navigator.clipboard.writeText(`ALTER TABLE properties ADD COLUMN IF NOT EXISTS project_name TEXT, ADD COLUMN IF NOT EXISTS developer TEXT, ADD COLUMN IF NOT EXISTS images TEXT[], ADD COLUMN IF NOT EXISTS rera_permit_no TEXT, ADD COLUMN IF NOT EXISTS reference_id TEXT; CREATE TABLE IF NOT EXISTS agents (id TEXT PRIMARY KEY, name TEXT NOT NULL, email TEXT UNIQUE NOT NULL, phone TEXT, whatsapp TEXT, photo_url TEXT, role TEXT DEFAULT 'agent', password TEXT, created_at TIMESTAMP WITH TIME ZONE DEFAULT CURRENT_TIMESTAMP);`); alert("Full SQL copied to clipboard."); }} className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-sm text-[10px] font-bold uppercase tracking-[0.2em] transition-all shadow-xl">Copy Global Schema Script</button>
                    <button onClick={runDiagnostics} className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-4 rounded-sm text-[10px] font-bold uppercase tracking-[0.2em] transition-all flex items-center gap-3">
                        <Activity size={16}/> Verify Database Health
                    </button>
                 </div>

                 {diagResults.length > 0 && (
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in">
                        {diagResults.map(res => (
                            <div key={res.table} className={`p-4 rounded border flex justify-between items-center ${res.status === 'ok' ? 'bg-green-900/20 border-green-800' : (res.status === 'checking' ? 'bg-slate-800 border-slate-700' : 'bg-red-900/20 border-red-800')}`}>
                                <span className="font-mono text-xs text-gray-300">TABLE: {res.table.toUpperCase()}</span>
                                {res.status === 'ok' ? <CheckCircle size={16} className="text-green-500"/> : (res.status === 'checking' ? <Loader2 size={16} className="animate-spin text-gray-500"/> : <AlertTriangle size={16} className="text-red-500"/>)}
                            </div>
                        ))}
                    </div>
                 )}
              </div>
           </div>
        )}

        {activeTab === 'leads' && (
           <div className="bg-white rounded border border-gray-100 shadow-sm overflow-hidden animate-fade-in"><div className="p-6 border-b bg-slate-50 flex justify-between items-center"><h3 className="font-serif text-xl font-bold">{currentAgent.role === 'admin' ? 'Global Portfolio Inquiries' : 'Leads for My Listings'}</h3></div><div className="overflow-x-auto"><table className="w-full text-left text-sm"><thead className="bg-slate-50 border-b"><tr className="text-[10px] font-bold uppercase text-gray-400"><th className="p-6">Client Identity</th><th className="p-6">Reference Asset</th><th className="p-6">Communication Method</th><th className="p-6">Inquiry Date</th><th className="p-6">Status</th></tr></thead><tbody>{leads.map(l => (<tr key={l.id} className="border-b hover:bg-slate-50 transition-all"><td className="p-6"><p className="font-bold text-slate-900">{l.name}</p><p className="text-[10px] text-gray-400 uppercase tracking-widest">Client Account</p></td><td className="p-6 text-xs font-mono text-slate-500">#{l.propertyId}</td><td className="p-6"><div className="flex flex-col gap-1"><span className="text-xs text-slate-700 flex items-center gap-2"><Mail size={12}/> {l.email}</span><span className="text-xs text-slate-400 flex items-center gap-2"><Smartphone size={12}/> {l.phone}</span></div></td><td className="p-6 text-xs text-gray-400">{l.createdAt ? new Date(l.createdAt).toLocaleDateString() : 'Real-time'}</td><td className="p-6"><span className="bg-blue-50 text-blue-600 text-[9px] font-bold px-3 py-1.5 rounded uppercase border border-blue-100">{l.status || 'NEW_INQUIRY'}</span></td></tr>))}{leads.length === 0 && (<tr><td colSpan={5} className="p-20 text-center text-gray-400 italic">No inquiries found for your current portfolio.</td></tr>)}</tbody></table></div></div>
        )}
      </div>
      {isSuccess && <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[10000] bg-green-600 text-white px-12 py-5 rounded-sm shadow-2xl flex items-center gap-4 animate-fade-in border-b-4 border-green-800"><CheckCircle size={24}/><span className="font-bold uppercase tracking-[0.2em] text-xs">System Synchronized Successfully</span></div>}
    </div>
  );
};

export default Admin;
